import React, { useContext, useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';

const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requireSuperAdmin = false,
  requireTournamentAdmin = false,
  requirePermission = null,
  requireAnyPermission = null,
  tournamentIdFromPath = false,
}) => {
  const {
    currentUser,
    userProfile,
    loading,
    profileLoading,
    isAdmin,
    isSuperAdmin,
    hasPermission,
    hasAccessToTournament,
  } = useContext(AuthContext);
  const location = useLocation();
  const params = useParams();

  // Get tournament ID from URL params if needed
  const tournamentId = tournamentIdFromPath ? params.tournamentId || params.id : null;

  // For debugging - remove this in production
  useEffect(() => {
    if (requireAdmin || requireSuperAdmin || requireTournamentAdmin) {
      // Debug logging can be added here if needed
    }
  }, [
    currentUser,
    userProfile,
    requireAdmin,
    requireSuperAdmin,
    requireTournamentAdmin,
    tournamentId,
  ]);

  if (loading || profileLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading user data...
        </Typography>
      </Box>
    );
  }

  // Not logged in - redirect to login
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Permission-gated route — super admin implicit via hasPermission()
  if (requirePermission) {
    if (!hasPermission(requirePermission)) {
      return <Navigate to="/" replace />;
    }
  }
  // Route gated behind ANY of several permissions (super admin implicit).
  else if (requireAnyPermission) {
    if (!requireAnyPermission.some((p) => hasPermission(p))) {
      return <Navigate to="/" replace />;
    }
  }
  // If this route requires super admin access specifically
  else if (requireSuperAdmin) {
    const superAdminStatus = isSuperAdmin();

    if (!superAdminStatus) {
      // User is logged in but not a super admin, redirect to home
      return <Navigate to="/" replace />;
    }
  }
  // If this route requires tournament admin access for a specific tournament
  else if (requireTournamentAdmin && tournamentIdFromPath) {
    if (!tournamentId) {
      // Tournament ID is required but not found in URL
      return <Navigate to="/admin/dashboard" replace />;
    }

    const hasAccess = hasAccessToTournament(tournamentId);

    if (!hasAccess) {
      // User doesn't have access to this tournament, redirect to dashboard
      return <Navigate to="/admin/dashboard" replace />;
    }
  }
  // If this route requires any admin access (backward compatibility)
  else if (requireAdmin) {
    const adminStatus = isAdmin();

    if (!adminStatus) {
      // User is logged in but not an admin, redirect to home
      return <Navigate to="/" replace />;
    }
  }

  // User is authenticated (and is an admin if required)
  return children;
};

export default ProtectedRoute;
